import { useEffect, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import Button from "../core/Button";
import {
  getReleaseNotesStatus,
  acknowledgeReleaseNotes,
  ReleaseNotesStatus,
} from "../../api/releaseNotesApi";
import { HiSparkles, HiCreditCard, HiShieldCheck } from "react-icons/hi";

const SLIDES = [
  {
    id: "ai-chat",
    icon: HiSparkles,
    title: "چت هوش مصنوعی جدید",
    description:
      "با قابلیت چت هوش مصنوعی، پاسخ‌های هوشمند و پیشنهادات شخصی‌سازی‌شده را در دسترس دارید. از طریق منو به بخش دستیار هوشمند بروید و سوالات خود را بپرسید.",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
  },
  {
    id: "wallet",
    icon: HiCreditCard,
    title: "تغییرات کیف پول",
    description:
      "کیف پول با امکانات جدید به‌روز شده است. مدیریت بهتر موجودی، نمایش شفاف‌تر تراکنش‌ها .",
    iconBg: "bg-primary-50",
    iconColor: "text-primary-600",
  },
  {
    id: "other",
    icon: HiShieldCheck,
    title: "سایر به‌روزرسانی‌ها",
    description:
      "سرعت بارگذاری سایت بهبود یافته و مشکلات امنیتی برطرف شده‌اند. تجربهٔ پایدارتر و امن‌تر برای شما.",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
  },
];

export default function ReleaseNotesPopup() {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [status, setStatus] = useState<ReleaseNotesStatus | null>(null);

  const acknowledgeAndClose = useCallback(async () => {
    if (!status?.current_version) return;
    await acknowledgeReleaseNotes(status.current_version);
    setVisible(false);
    setCurrentSlide(0);
  }, [status?.current_version]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getReleaseNotesStatus();
      if (cancelled) return;
      setLoading(false);
      if (!error && data && !data.user_has_acknowledged) {
        setStatus(data);
        setVisible(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClose = useCallback(() => {
    acknowledgeAndClose();
  }, [acknowledgeAndClose]);

  const handleNext = () => {
    if (currentSlide < SLIDES.length - 1) {
      setCurrentSlide((i) => i + 1);
    } else {
      acknowledgeAndClose();
    }
  };

  if (loading) return null;

  const slide = SLIDES[currentSlide];
  const isLastSlide = currentSlide === SLIDES.length - 1;
  const Icon = slide.icon;

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      header={
        <div className="flex justify-between items-center w-full gap-2">
          <span className="text-Title/20/Bold text-gray-900">
            به‌روزرسانی نسخه ۱.۲.۰
          </span>
        </div>
      }
      modal
      className="release-notes-dialog p-fluid"
      contentClassName="release-notes-dialog-content"
      maskClassName="release-notes-dialog-mask"
      blockScroll
      style={{ width: "90vw", maxWidth: "480px", direction: "rtl" }}
      breakpoints={{ "960px": "95vw", "641px": "95vw" }}
      draggable={false}
      resizable={false}
      closable={true}
      dismissableMask={false}
    >
      <div className="flex flex-col gap-6 text-right">
        {/* Slide content */}
        <div className="min-h-[200px] flex flex-col items-center text-center">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${slide.iconBg} ${slide.iconColor}`}
          >
            <Icon className="w-8 h-8" />
          </div>
          <h3 className="text-SubHeading/18/Bold text-gray-900 mb-2">
            {slide.title}
          </h3>
          <p className="text-BodyText/14/Regular text-gray-700 leading-7">
            {slide.description}
          </p>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`اسلاید ${i + 1}`}
              onClick={() => setCurrentSlide(i)}
              className={`h-2 rounded-full transition-all duration-200 ${
                i === currentSlide
                  ? "w-6 bg-primary-500"
                  : "w-2 bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <Button
            size="m"
            color="purple"
            label={isLastSlide ? "متوجه شدم" : "بعدی"}
            type="button"
            onClick={handleNext}
          />
        </div>
      </div>
    </Dialog>
  );
}
